import React, { useEffect, useMemo, useRef, useState } from 'react';
import { vuser } from '../../services/api';
import type { Persona, VirtualUser, VirtualUserType, PersonaReactionRules } from '../../types/vuser';
import {
  mergeMetaPromptsWithFinalStepDefault,
  normalizeLegacyRagDocIds,
  stripRagDocIdsForApi,
  type AgentConfiguratorState,
} from '../../types/agentConfigurator';
import MultiSelect, { type MultiSelectOption } from './MultiSelect';
import MiniAgentConfigurator from './MiniAgentConfigurator';
import ConfirmModal from '../ConfirmModal';
import TruncatedText from './TruncatedText';
import { capitalizeError } from '../../utils/errorUtils';
import { getAgentNameUniquenessError } from '../../utils/agentNameUniqueness';
import { NOTIFICATION_AUTO_HIDE_MS } from '../../constants/uiTiming';
import { MAX_VIRTUAL_USER_SYNC_WORKERS } from '../../constants/agentFieldLimits';
import {
  DEFAULT_VUSER_USER_PROMPT_FIELDS,
  buildVirtualUserUserTypePromptApiPayload,
  resolveVirtualUserPromptFields,
} from '../../constants/vuserDefaults';
import {
  DEFAULT_PERSONA_JUDGE_META_PROMPTS,
  createDefaultPersonaFormState,
  resolvePersonaJudgeMetaPrompts,
  type PersonaFormState,
} from '../../utils/personaDefaults';
import './VUserPanel.css';

/** Same limit as MiniAgentConfigurator meta prompts (FINAL_STEP_PROMPT, RAG_PROMPT). */
const VU_PROMPT_MAX = 5000;

function VuPromptCharCount({ length, max }: { length: number; max: number }) {
  const nearLimit = length > max * 0.9;
  const atLimit = length >= max;
  const mod = atLimit ? 'vuser-prompt-count--limit' : nearLimit ? 'vuser-prompt-count--near' : 'vuser-prompt-count--ok';
  return (
    <div className={`vuser-prompt-count ${mod}`}>
      {length}/{max}
    </div>
  );
}

function isValidJsonString(s: string): boolean {
  try {
    JSON.parse(s);
    return true;
  } catch {
    return false;
  }
}

function asJsonString(input: any, fallback = '{}'): string {
  if (typeof input === 'string' && input.trim()) return input;
  try {
    return JSON.stringify(input ?? JSON.parse(fallback));
  } catch {
    return fallback;
  }
}

function parseAiConfig(jsonString: string | null | undefined): AgentConfiguratorState | null {
  if (!jsonString) return null;
  try {
    const parsed = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
    // Если это старый формат (просто model и temperature), конвертируем в новый
    if (parsed?.model && typeof parsed.temperature === 'number' && !parsed.coordinator) {
      return {
        coordinator: {
          name: 'AgentCoordinator',
          description: 'Coordinator node that communicates with worker nodes to respond to user messages.',
          prompt: "You are the coordinator of workers. Use them to respond to the user's message.",
          model: parsed.model,
          temperature: parsed.temperature,
          use_rag: false,
          use_ltm: false,
        },
        workers: [],
        metaPrompts: mergeMetaPromptsWithFinalStepDefault({}),
      };
    }
    // Если это новый формат (AgentConfiguratorState)
    if (parsed?.coordinator && Array.isArray(parsed.workers)) {
      const mp = parsed.metaPrompts || {};
      return {
        coordinator: {
          ...parsed.coordinator,
          use_rag: parsed.coordinator.use_rag === true,
          use_ltm: parsed.coordinator.use_ltm === true,
          rag_doc_ids: normalizeLegacyRagDocIds(parsed.coordinator.rag_doc_ids),
        },
        workers: parsed.workers
          .filter((w: any) => w.mode === 'synchronous') // Только синхронные
          .slice(0, MAX_VIRTUAL_USER_SYNC_WORKERS)
          .map((w: any) => ({
            ...w,
            mode: 'synchronous' as const,
            use_rag: w.use_rag === true,
            use_ltm: w.use_ltm === true,
            rag_doc_ids: normalizeLegacyRagDocIds(w.rag_doc_ids),
          })),
        metaPrompts: mergeMetaPromptsWithFinalStepDefault(mp),
      };
    }
    return null;
  } catch {
    return null;
  }
}

/** Empty or whitespace-only → `null` for PUT (backend clears prompt fields when omitted; we send explicit nulls). */
function trimVirtualUserPromptField(s: string): string | null {
  const t = s.trim();
  return t === '' ? null : t;
}

function extractCorePromptFromUserConfig(userConfigRaw: VirtualUser['user_config']): string {
  if (userConfigRaw == null) return '';
  try {
    const parsed = typeof userConfigRaw === 'string' ? JSON.parse(userConfigRaw) : userConfigRaw;
    const mp = parsed?.metaPrompts;
    if (mp && typeof mp === 'object' && typeof (mp as Record<string, unknown>).CORE_PROMPT === 'string') {
      return String((mp as Record<string, string>).CORE_PROMPT).trim();
    }
  } catch {
    // ignore
  }
  return '';
}

/** Virtual admin: role text lives in `virtual_user_system_prompt` and is mirrored to `metaPrompts.CORE_PROMPT` in user_config. */
function mergeAdminMetaCorePrompt(
  metaPromptsInput: AgentConfiguratorState['metaPrompts'] | undefined,
  coreText: string | null
): Record<string, string> {
  const base = { ...mergeMetaPromptsWithFinalStepDefault(metaPromptsInput ?? {}) };
  if (coreText !== null && coreText.trim() !== '') {
    base.CORE_PROMPT = coreText.trim();
  } else {
    delete base.CORE_PROMPT;
  }
  return mergeMetaPromptsWithFinalStepDefault(base);
}

function formatVirtualUserCreatedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function createDefaultAiConfig(): AgentConfiguratorState {
  return {
    coordinator: {
      name: 'AgentCoordinator',
      description: 'Coordinator node that communicates with worker nodes to respond to user messages.',
      prompt: "You are the coordinator of workers. Use them to respond to the user's message.",
      model: 'gpt-4.1-mini',
      temperature: 0.7,
      use_rag: false,
      use_ltm: false,
    },
    workers: [],
    metaPrompts: mergeMetaPromptsWithFinalStepDefault({}),
  };
}

type VUserToolPreferences = {
  memory_enabled: boolean;
  diary_enabled: boolean;
  portrait_enabled: boolean;
};

const DEFAULT_VUSER_TOOL_PREFERENCES: VUserToolPreferences = {
  memory_enabled: true,
  diary_enabled: true,
  portrait_enabled: true,
};

function createDefaultCreateVuForm(): {
  type: VirtualUserType;
  name: string;
  description: string;
  ai_config: AgentConfiguratorState;
  virtual_user_system_prompt: string;
  first_user_message_instruction: string;
  followup_user_message_instruction: string;
} & VUserToolPreferences {
  return {
    type: 'user',
    name: '',
    description: '',
    ai_config: createDefaultAiConfig(),
    ...DEFAULT_VUSER_USER_PROMPT_FIELDS,
    ...DEFAULT_VUSER_TOOL_PREFERENCES,
  };
}

function createDefaultEditVuForm(): {
  name: string;
  description: string;
  ai_config: AgentConfiguratorState;
  virtual_user_system_prompt: string;
  first_user_message_instruction: string;
  followup_user_message_instruction: string;
} & VUserToolPreferences {
  return {
    name: '',
    description: '',
    ai_config: createDefaultAiConfig(),
    ...DEFAULT_VUSER_USER_PROMPT_FIELDS,
    ...DEFAULT_VUSER_TOOL_PREFERENCES,
  };
}

function readVUserToolPreferences(vu: Partial<VirtualUser> | null | undefined): VUserToolPreferences {
  return {
    memory_enabled: vu?.memory_enabled !== false,
    diary_enabled: vu?.diary_enabled !== false,
    portrait_enabled: vu?.portrait_enabled !== false,
  };
}

function VUserToolPreferencesFields({
  value,
  onChange,
}: {
  value: VUserToolPreferences;
  onChange: (next: VUserToolPreferences) => void;
}) {
  const items: Array<{ key: keyof VUserToolPreferences; label: string; help: string }> = [
    {
      key: 'memory_enabled',
      label: 'Use memory',
      help: 'AI assistant can use long-term memory in generated test dialogs.',
    },
    {
      key: 'diary_enabled',
      label: 'Use Diary',
      help: 'AI assistant can read and write diary entries for the synthetic test user.',
    },
    {
      key: 'portrait_enabled',
      label: 'Use Portrait',
      help: 'AI assistant can read and update portrait data for the synthetic test user.',
    },
  ];

  return (
    <div className="vuser-preferences-block">
      <div className="vuser-preferences-block__heading">
        <div className="vuser-preferences-block__title">Settings</div>
        <div className="vuser-preferences-block__subtitle">Preferences</div>
      </div>
      <div className="vuser-preferences-list">
        {items.map((item) => (
          <label key={item.key} className="vuser-preference-toggle" title={item.help}>
            <input
              type="checkbox"
              checked={value[item.key]}
              onChange={() => onChange({ ...value, [item.key]: !value[item.key] })}
            />
            <span className="vuser-preference-toggle__switch" aria-hidden="true" />
            <span className="vuser-preference-toggle__content">
              <span className="vuser-preference-toggle__label">{item.label}</span>
              <span className="vuser-preference-toggle__help">{item.help}</span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

function parseReactionRules(jsonString: string): {
  like_probability: number;
  dislike_probability: number;
  comment_probability: number;
  reference_answer_probability: number;
} {
  try {
    const parsed = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
    return {
      like_probability: typeof parsed?.like_probability === 'number' ? parsed.like_probability : 0.6,
      dislike_probability: typeof parsed?.dislike_probability === 'number' ? parsed.dislike_probability : 0.1,
      comment_probability: typeof parsed?.comment_probability === 'number' ? parsed.comment_probability : 0.2,
      reference_answer_probability: typeof parsed?.reference_answer_probability === 'number' ? parsed.reference_answer_probability : 0.1,
    };
  } catch {
    return {
      like_probability: 0.6,
      dislike_probability: 0.1,
      comment_probability: 0.2,
      reference_answer_probability: 0.1,
    };
  }
}

export interface VUserPanelProps {
  integrationId: string | null;
  onOpenOnboarding?: () => void;
}

const VUserPanel: React.FC<VUserPanelProps> = ({ integrationId, onOpenOnboarding }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [virtualUsers, setVirtualUsers] = useState<VirtualUser[]>([]);
  const [selectedVirtualUserId, setSelectedVirtualUserId] = useState<string | null>(null);
  const selectedVirtualUser = useMemo(
    () => (selectedVirtualUserId ? virtualUsers.find((u) => u.id === selectedVirtualUserId) || null : null),
    [virtualUsers, selectedVirtualUserId]
  );

  const [personas, setPersonas] = useState<Persona[]>([]);
  const personasSectionRef = useRef<HTMLDivElement | null>(null);
  
  // Маппинг persona_id -> persona name для отображения в таблице
  const [personaMap, setPersonaMap] = useState<Map<string, string>>(new Map());


  // Delete confirmation modals state
  const [deletePersonaModal, setDeletePersonaModal] = useState<{
    isOpen: boolean;
    personaId: string | null;
  }>({
    isOpen: false,
    personaId: null,
  });

  const [deleteVirtualUserModal, setDeleteVirtualUserModal] = useState<{
    isOpen: boolean;
    virtualUserId: string | null;
  }>({
    isOpen: false,
    virtualUserId: null,
  });

  const [createVuOpen, setCreateVuOpen] = useState(false);
  const [createVuError, setCreateVuError] = useState<string | null>(null);
  const [createVu, setCreateVu] = useState<{
    type: VirtualUserType;
    name: string;
    description: string;
    ai_config: AgentConfiguratorState | null;
    virtual_user_system_prompt: string;
    first_user_message_instruction: string;
    followup_user_message_instruction: string;
  } & VUserToolPreferences>(createDefaultCreateVuForm());

  const [editVuOpen, setEditVuOpen] = useState(false);
  const [editVuError, setEditVuError] = useState<string | null>(null);
  const [editingVuId, setEditingVuId] = useState<string | null>(null);
  const [editVu, setEditVu] = useState<{
    name: string;
    description: string;
    ai_config: AgentConfiguratorState | null;
    virtual_user_system_prompt: string;
    first_user_message_instruction: string;
    followup_user_message_instruction: string;
  } & VUserToolPreferences>(createDefaultEditVuForm());

  const [createPersonaOpen, setCreatePersonaOpen] = useState(false);
  const [createPersonaError, setCreatePersonaError] = useState<string | null>(null);
  const [createPersona, setCreatePersona] = useState<PersonaFormState>(createDefaultPersonaFormState());

  const [editPersonaOpen, setEditPersonaOpen] = useState(false);
  const [editPersonaError, setEditPersonaError] = useState<string | null>(null);
  const [editingPersonaId, setEditingPersonaId] = useState<string | null>(null);
  const [editPersona, setEditPersona] = useState<PersonaFormState>(createDefaultPersonaFormState());

  const [assignPersonaId, setAssignPersonaId] = useState<string>('');

  // Keep errors visible until the next action; success auto-hides.
  const clearSuccessSoon = () => setTimeout(() => setSuccess(null), NOTIFICATION_AUTO_HIDE_MS);

  const loadVirtualUsers = async () => {
    if (!integrationId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await vuser.listVirtualUsers(integrationId);
      const users = resp.data || [];
      setVirtualUsers(users);
      
      // Загружаем персоны для всех виртуальных пользователей параллельно
      // чтобы создать маппинг persona_id -> persona name
      const personaMapPromises = users
        .filter(u => u.persona_id)
        .map(async (u) => {
          try {
            const personasResp = await vuser.listPersonas(integrationId, u.id);
            const userPersonas = personasResp.data || [];
            const persona = userPersonas.find(p => p.id === u.persona_id);
            return persona ? [u.persona_id, persona.name] as [string, string] : null;
          } catch {
            return null;
          }
        });
      
      const personaEntries = (await Promise.all(personaMapPromises))
        .filter((entry): entry is [string, string] => entry !== null);
      
      setPersonaMap(new Map(personaEntries));
    } catch (e: any) {
      setVirtualUsers([]);
      setError(capitalizeError(e?.message) || 'Failed to load virtual users');
    } finally {
      setLoading(false);
    }
  };

  const loadPersonas = async (virtualUserId: string) => {
    if (!integrationId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await vuser.listPersonas(integrationId, virtualUserId);
      setPersonas(resp.data || []);
    } catch (e: any) {
      setPersonas([]);
      setError(capitalizeError(e?.message) || 'Failed to load personas');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    setSelectedVirtualUserId(null);
    setPersonas([]);
    setPersonaMap(new Map());
    // Clear all errors when integration changes
    setError(null);
    setCreateVuError(null);
    setEditVuError(null);
    setCreatePersonaError(null);
    setEditPersonaError(null);
    // Close all modals when integration changes
    setCreateVuOpen(false);
    setEditVuOpen(false);
    setCreatePersonaOpen(false);
    setEditPersonaOpen(false);
    if (integrationId) {
      void loadVirtualUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationId]);

  useEffect(() => {
    if (!selectedVirtualUserId) return;
    void loadPersonas(selectedVirtualUserId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVirtualUserId]);

  // Block body scroll when any modal is open
  useEffect(() => {
    const isModalOpen = createVuOpen || editVuOpen || createPersonaOpen || editPersonaOpen;
    if (isModalOpen) {
      // Save current scroll position (if not already saved)
      const scrollY = window.scrollY;
      if (document.body.style.position !== 'fixed') {
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
      }
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        const savedScrollY = parseInt(scrollY || '0') * -1;
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          window.scrollTo(0, savedScrollY);
        });
      }
    }
    return () => {
      // Cleanup on unmount
      if (!isModalOpen) {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
      }
    };
  }, [createVuOpen, editVuOpen, createPersonaOpen, editPersonaOpen]);

  const handleCreateVirtualUser = async () => {
    if (!integrationId) return;
    const { type, name, description, ai_config } = createVu;
    if (!name.trim()) {
      setCreateVuError('Name is required');
      return;
    }
    if (name.length > 200) {
      setCreateVuError('Name must not exceed 200 characters');
      return;
    }
    if (description.length > 200) {
      setCreateVuError('Description must not exceed 200 characters');
      return;
    }
    const finalAiConfig = ai_config || createDefaultAiConfig();
    const agentNameError = getAgentNameUniquenessError(
      finalAiConfig.coordinator?.name,
      finalAiConfig.workers || []
    );
    if (agentNameError) {
      setCreateVuError(agentNameError);
      return;
    }
    setLoading(true);
    setCreateVuError(null);
    try {
      // Формируем user_config согласно API: coordinator, workers и metaPrompts должны быть внутри user_config
      const metaPromptsForApi =
        type === 'admin'
          ? mergeAdminMetaCorePrompt(
              finalAiConfig.metaPrompts,
              trimVirtualUserPromptField(createVu.virtual_user_system_prompt)
            )
          : mergeMetaPromptsWithFinalStepDefault(finalAiConfig.metaPrompts);
      const userConfigForApi = {
        coordinator: stripRagDocIdsForApi({ ...(finalAiConfig.coordinator as Record<string, unknown>) }),
        workers: (finalAiConfig.workers || []).map((w) => stripRagDocIdsForApi({ ...(w as Record<string, unknown>) })),
        metaPrompts: metaPromptsForApi,
      };
      const userConfigJson = JSON.stringify(userConfigForApi);
      const createBody: Parameters<typeof vuser.createVirtualUser>[1] = {
        type,
        name,
        description,
        user_config: userConfigJson,
        memory_enabled: createVu.memory_enabled,
        diary_enabled: createVu.diary_enabled,
        portrait_enabled: createVu.portrait_enabled,
      };
      if (type === 'admin') {
        const p1 = trimVirtualUserPromptField(createVu.virtual_user_system_prompt);
        if (p1 !== null) createBody.virtual_user_system_prompt = p1;
      } else {
        Object.assign(createBody, buildVirtualUserUserTypePromptApiPayload(createVu));
      }
      await vuser.createVirtualUser(integrationId, createBody);
      setSuccess('Virtual user created');
      clearSuccessSoon();
      setCreateVuOpen(false);
      setCreateVuError(null);
      // Reset form after successful creation
      setCreateVu(createDefaultCreateVuForm());
      await loadVirtualUsers();
    } catch (e: any) {
      const errorMessage = e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || 'Failed to create virtual user';
      setCreateVuError(capitalizeError(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  const handleEditVirtualUser = async () => {
    if (!integrationId || !editingVuId) return;
    const { name, description, ai_config } = editVu;
    if (!name.trim()) {
      setEditVuError('Name is required');
      return;
    }
    if (name.length > 200) {
      setEditVuError('Name must not exceed 200 characters');
      return;
    }
    if (description.length > 200) {
      setEditVuError('Description must not exceed 200 characters');
      return;
    }
    const finalAiConfig = ai_config || createDefaultAiConfig();
    const agentNameError = getAgentNameUniquenessError(
      finalAiConfig.coordinator?.name,
      finalAiConfig.workers || []
    );
    if (agentNameError) {
      setEditVuError(agentNameError);
      return;
    }
    setLoading(true);
    setEditVuError(null);
    try {
      // Формируем user_config согласно API: coordinator, workers и metaPrompts должны быть внутри user_config
      const editingType = virtualUsers.find((u) => u.id === editingVuId)?.type;
      const metaPromptsForApi =
        editingType === 'admin'
          ? mergeAdminMetaCorePrompt(
              finalAiConfig.metaPrompts,
              trimVirtualUserPromptField(editVu.virtual_user_system_prompt)
            )
          : mergeMetaPromptsWithFinalStepDefault(finalAiConfig.metaPrompts);
      const userConfigForApi = {
        coordinator: stripRagDocIdsForApi({ ...(finalAiConfig.coordinator as Record<string, unknown>) }),
        workers: (finalAiConfig.workers || []).map((w) => stripRagDocIdsForApi({ ...(w as Record<string, unknown>) })),
        metaPrompts: metaPromptsForApi,
      };
      const userConfigJson = JSON.stringify(userConfigForApi);
      const updateBody: Parameters<typeof vuser.updateVirtualUser>[2] = {
        name,
        description,
        user_config: userConfigJson,
        virtual_user_system_prompt: trimVirtualUserPromptField(editVu.virtual_user_system_prompt),
        memory_enabled: editVu.memory_enabled,
        diary_enabled: editVu.diary_enabled,
        portrait_enabled: editVu.portrait_enabled,
      };
      if (editingType !== 'admin') {
        Object.assign(updateBody, buildVirtualUserUserTypePromptApiPayload(editVu));
      }
      await vuser.updateVirtualUser(integrationId, editingVuId, updateBody);
      setSuccess('Virtual user updated');
      clearSuccessSoon();
      setEditVuOpen(false);
      setEditingVuId(null);
      setEditVuError(null);
      // Reset form after successful update
      setEditVu(createDefaultEditVuForm());
      await loadVirtualUsers();
    } catch (e: any) {
      const errorMessage = e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || 'Failed to update virtual user';
      setEditVuError(capitalizeError(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = async (virtualUser: VirtualUser) => {
    setEditingVuId(virtualUser.id);
    let vu: VirtualUser = virtualUser;
    if (integrationId) {
      try {
        const resp = await vuser.getVirtualUser(integrationId, virtualUser.id);
        if (resp?.data) vu = resp.data;
      } catch {
        // use list row
      }
    }
    // Пытаемся извлечь coordinator, workers и metaPrompts из user_config (новый формат API)
    let parsedAiConfig: AgentConfiguratorState | null = null;
    try {
      const userConfigParsed = typeof vu.user_config === 'string' ? JSON.parse(vu.user_config) : vu.user_config;
      if (userConfigParsed?.coordinator) {
        const mpUc = userConfigParsed.metaPrompts || {};
        parsedAiConfig = {
          coordinator: {
            ...userConfigParsed.coordinator,
            use_rag: userConfigParsed.coordinator.use_rag === true,
            use_ltm: userConfigParsed.coordinator.use_ltm === true,
            rag_doc_ids: normalizeLegacyRagDocIds(userConfigParsed.coordinator.rag_doc_ids),
          },
          workers: (userConfigParsed.workers || [])
            .filter((w: any) => w.mode === 'synchronous') // Только синхронные
            .slice(0, MAX_VIRTUAL_USER_SYNC_WORKERS)
            .map((w: any) => ({
              ...w,
              mode: 'synchronous' as const,
              use_rag: w.use_rag === true,
              use_ltm: w.use_ltm === true,
              rag_doc_ids: normalizeLegacyRagDocIds(w.rag_doc_ids),
            })),
          metaPrompts: mergeMetaPromptsWithFinalStepDefault(mpUc),
        };
      }
    } catch {
      // Если не удалось распарсить из user_config, пробуем ai_config
    }
    if (!parsedAiConfig) {
      parsedAiConfig = parseAiConfig(vu.ai_config);
    }
    const systemPromptInitial =
      vu.type === 'admin'
        ? (vu.virtual_user_system_prompt ?? '').trim() || extractCorePromptFromUserConfig(vu.user_config)
        : vu.virtual_user_system_prompt ?? '';
    const promptFields =
      vu.type === 'admin'
        ? {
            virtual_user_system_prompt: systemPromptInitial,
            first_user_message_instruction: '',
            followup_user_message_instruction: '',
          }
        : resolveVirtualUserPromptFields(vu);
    setEditVu({
      name: vu.name || '',
      description: vu.description || '',
      ai_config: parsedAiConfig || createDefaultAiConfig(),
      ...promptFields,
      ...readVUserToolPreferences(vu),
    });
    setEditVuOpen(true);
  };

  const handleCreatePersona = async () => {
    if (!integrationId || !selectedVirtualUser) return;
    if (!createPersona.name.trim()) {
      setCreatePersonaError('Persona name is required');
      return;
    }
    if (createPersona.name.length > 200) {
      setCreatePersonaError('Name must not exceed 200 characters');
      return;
    }
    if (createPersona.description.length > 200) {
      setCreatePersonaError('Description must not exceed 200 characters');
      return;
    }
    const personaType = selectedVirtualUser.type === 'admin' ? 'virtual_admin' : 'virtual_user';
    setLoading(true);
    setCreatePersonaError(null);
    try {
      // Убеждаемся, что reaction_rules содержит только вероятности
      const reactionRules = {
        like_probability: createPersona.reaction_rules.like_probability ?? 0.6,
        dislike_probability: createPersona.reaction_rules.dislike_probability ?? 0.1,
        comment_probability: createPersona.reaction_rules.comment_probability ?? 0.2,
        reference_answer_probability: createPersona.reaction_rules.reference_answer_probability ?? 0.1,
      };
      
      const judgeMetaPrompts = resolvePersonaJudgeMetaPrompts(createPersona.judge_meta_prompts);
      
      const createResp = await vuser.createPersona(integrationId, {
        virtual_user_id: selectedVirtualUser.id,
        name: createPersona.name,
        description: createPersona.description,
        type: personaType,
        reaction_rules: reactionRules,
        judge_meta_prompts: judgeMetaPrompts,
      } as any);
      setCreatePersonaOpen(false);
      setCreatePersonaError(null);
      setCreatePersona(createDefaultPersonaFormState());
      await loadPersonas(selectedVirtualUser.id);

      // Auto-assign the first persona so the user doesn't have to do it manually
      const createdPersona = createResp?.data;
      const isFirstPersona = !selectedVirtualUser.persona_id;
      if (isFirstPersona && createdPersona?.id) {
        try {
          await vuser.setPersona(integrationId, selectedVirtualUser.id, createdPersona.id);
          setAssignPersonaId(createdPersona.id);
          setPersonaMap(prev => new Map(prev).set(createdPersona.id, createdPersona.name));
          setSuccess('Persona created and auto-assigned');
        } catch {
          setSuccess('Persona created (auto-assign failed — please assign manually)');
        }
      } else {
        setAssignPersonaId(createdPersona?.id || '');
        setSuccess('Persona created');
      }
      clearSuccessSoon();
      
      // Перезагружаем виртуальных пользователей для обновления маппинга
      await loadVirtualUsers();
    } catch (e: any) {
      const errorMessage = e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || 'Failed to create persona';
      setCreatePersonaError(capitalizeError(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  const handleAssignPersona = async () => {
    if (!integrationId || !selectedVirtualUserId) return;
    if (!assignPersonaId) {
      setError('Select persona');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await vuser.setPersona(integrationId, selectedVirtualUserId, assignPersonaId);
      setSuccess('Persona assigned');
      clearSuccessSoon();
      
      // Обновляем маппинг персоны
      const selectedPersona = personas.find(p => p.id === assignPersonaId);
      if (selectedPersona) {
        setPersonaMap(prev => new Map(prev).set(assignPersonaId, selectedPersona.name));
      }
      
      await loadVirtualUsers();
    } catch (e: any) {
      setError(capitalizeError(e?.message) || 'Failed to assign persona');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditPersonaModal = (persona: Persona) => {
    setEditingPersonaId(persona.id);
    const reactionRulesData = typeof persona.reaction_rules === 'string' 
      ? JSON.parse(persona.reaction_rules)
      : persona.reaction_rules;
    
    // Извлекаем только вероятности из reaction_rules
    const reactionRules = {
      like_probability: reactionRulesData?.like_probability ?? 0.6,
      dislike_probability: reactionRulesData?.dislike_probability ?? 0.1,
      comment_probability: reactionRulesData?.comment_probability ?? 0.2,
      reference_answer_probability: reactionRulesData?.reference_answer_probability ?? 0.1,
    };
    
    const rawJudgeMetaPrompts = persona.judge_meta_prompts
      ? {
          like_prompt: persona.judge_meta_prompts.like_prompt || '',
          dislike_prompt: persona.judge_meta_prompts.dislike_prompt || '',
          comment_prompt: persona.judge_meta_prompts.comment_prompt || '',
          reference_answer_prompt: persona.judge_meta_prompts.reference_answer_prompt || '',
        }
      : {
          like_prompt: reactionRulesData?.likes_prompt || '',
          dislike_prompt: reactionRulesData?.dislikes_prompt || '',
          comment_prompt: reactionRulesData?.comment_prompt || '',
          reference_answer_prompt: reactionRulesData?.reference_answer_prompt || '',
        };

    setEditPersona({
      name: persona.name || '',
      description: persona.description || '',
      reaction_rules: reactionRules,
      judge_meta_prompts: resolvePersonaJudgeMetaPrompts(rawJudgeMetaPrompts),
    });
    setEditPersonaOpen(true);
  };

  const handleEditPersona = async () => {
    if (!integrationId || !editingPersonaId) return;
    if (!editPersona.name.trim()) {
      setEditPersonaError('Persona name is required');
      return;
    }
    if (editPersona.name.length > 200) {
      setEditPersonaError('Name must not exceed 200 characters');
      return;
    }
    if (editPersona.description.length > 200) {
      setEditPersonaError('Description must not exceed 200 characters');
      return;
    }
    setLoading(true);
    setEditPersonaError(null);
    try {
      // Убеждаемся, что все поля reaction_rules определены (только вероятности)
      const reactionRules = {
        like_probability: editPersona.reaction_rules.like_probability ?? 0.6,
        dislike_probability: editPersona.reaction_rules.dislike_probability ?? 0.1,
        comment_probability: editPersona.reaction_rules.comment_probability ?? 0.2,
        reference_answer_probability: editPersona.reaction_rules.reference_answer_probability ?? 0.1,
      };
      
      const judgeMetaPrompts = resolvePersonaJudgeMetaPrompts(editPersona.judge_meta_prompts);
      
      await vuser.updatePersona(integrationId, editingPersonaId, {
        name: editPersona.name,
        description: editPersona.description,
        reaction_rules: reactionRules,
        judge_meta_prompts: judgeMetaPrompts,
      } as any);
      setSuccess('Persona updated');
      clearSuccessSoon();
      setEditPersonaOpen(false);
      setEditingPersonaId(null);
      setEditPersonaError(null);
      setEditPersona(createDefaultPersonaFormState());
      if (selectedVirtualUserId) {
        await loadPersonas(selectedVirtualUserId);
      }
      
      // Обновляем маппинг для обновленной персоны
      if (editingPersonaId) {
        setPersonaMap(prev => {
          const newMap = new Map(prev);
          if (newMap.has(editingPersonaId)) {
            newMap.set(editingPersonaId, editPersona.name);
          }
          return newMap;
        });
      }
      
      // Перезагружаем виртуальных пользователей для обновления маппинга
      await loadVirtualUsers();
    } catch (e: any) {
      const errorMessage = e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || 'Failed to update persona';
      setEditPersonaError(capitalizeError(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePersona = (id: string) => {
    if (!integrationId) return;
    setDeletePersonaModal({
      isOpen: true,
      personaId: id,
    });
  };

  const confirmDeletePersona = async () => {
    const personaId = deletePersonaModal.personaId;
    if (!integrationId || !personaId) return;

    setDeletePersonaModal({ isOpen: false, personaId: null });
    setLoading(true);
    setError(null);
    try {
      await vuser.deletePersona(integrationId, personaId);
      setSuccess('Persona deleted');
      clearSuccessSoon();
      if (selectedVirtualUserId) {
        await loadPersonas(selectedVirtualUserId);
      }
      
      // Удаляем персону из маппинга
      setPersonaMap(prev => {
        const newMap = new Map(prev);
        newMap.delete(personaId);
        return newMap;
      });
      
      // Перезагружаем виртуальных пользователей для обновления маппинга
      await loadVirtualUsers();
    } catch (e: any) {
      setError(capitalizeError(e?.message) || 'Failed to delete persona');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVirtualUser = (id: string) => {
    if (!integrationId) return;
    setDeleteVirtualUserModal({
      isOpen: true,
      virtualUserId: id,
    });
  };

  const confirmDeleteVirtualUser = async () => {
    const virtualUserId = deleteVirtualUserModal.virtualUserId;
    if (!integrationId || !virtualUserId) return;

    setDeleteVirtualUserModal({ isOpen: false, virtualUserId: null });
    setLoading(true);
    setError(null);
    try {
      await vuser.deleteVirtualUser(integrationId, virtualUserId);
      setSuccess('Virtual user deleted');
      clearSuccessSoon();
      if (selectedVirtualUserId === virtualUserId) setSelectedVirtualUserId(null);
      await loadVirtualUsers();
    } catch (e: any) {
      setError(capitalizeError(e?.message) || 'Failed to delete virtual user');
    } finally {
      setLoading(false);
    }
  };


  // Clear errors when component unmounts or integrationId becomes null
  useEffect(() => {
    if (!integrationId) {
      setError(null);
      setCreateVuError(null);
      setEditVuError(null);
      setCreatePersonaError(null);
      setEditPersonaError(null);
    }
  }, [integrationId]);

  if (!integrationId) {
    return null;
  }

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2>Virtual User Service</h2>
        <div className="header-actions">
          {onOpenOnboarding && (
            <button
              onClick={onOpenOnboarding}
              className="action-btn"
              style={{ background: '#0099FA', color: '#FFFFFF' }}
              title="Start guided tour"
            >
              <i className="fas fa-question-circle" aria-hidden="true"></i>
              <span className="btn-text">Help</span>
            </button>
          )}
          <button onClick={() => void loadVirtualUsers()} className="action-btn refresh-btn" disabled={loading}>
            <i className="fas fa-sync-alt" aria-hidden="true"></i>
            <span className="btn-text">Refresh users</span>
          </button>
        </div>
      </div>

      {error && !createVuOpen && !editVuOpen && !createPersonaOpen && !editPersonaOpen && (
        <div className="admin-message error" style={{ margin: 0, marginBottom: 16 }}>
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}
      {success && (
        <div className="admin-message success" style={{ margin: 0, marginBottom: 16 }}>
          <i className="fas fa-check-circle"></i> {success}
        </div>
      )}


      {/* Virtual Users */}
      <div style={{ marginBottom: 24 }}>
        <div className="admin-section-header" style={{ marginBottom: 12 }}>
          <h2 style={{ fontSize: 20 }}>Virtual Users ({virtualUsers.length})</h2>
          <div className="header-actions">
            <button
              id="vuser-create-btn"
              className="action-btn apply-btn"
              onClick={() => {
                // Reset form when opening modal
                setCreateVu(createDefaultCreateVuForm());
                setCreateVuOpen(true);
              }}
              disabled={loading}
            >
              <i className="fas fa-plus" aria-hidden="true"></i>
              <span className="btn-text">Create</span>
            </button>
          </div>
        </div>
        <div className="admin-table-wrapper">
          <table id="vuser-table" className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Persona</th>
                <th>Created</th>
                <th>ID</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {virtualUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-cell">
                    <div className="empty-state">
                      <div className="empty-title">No virtual users</div>
                      <div className="empty-subtitle">Create one to start.</div>
                    </div>
                  </td>
                </tr>
              ) : (
                virtualUsers.map((u) => (
                  <tr key={u.id} className={selectedVirtualUserId === u.id ? 'selected-row' : undefined}>
                    <td data-label="Name">{u.name}</td>
                    <td data-label="Type">{u.type}</td>
                    <td data-label="Persona">
                      {u.persona_id ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <span title={u.persona_id}>
                            {personaMap.get(u.persona_id) || u.persona_id}
                          </span>
                          {personaMap.get(u.persona_id) && (
                            <span style={{ fontSize: '11px', color: '#7F9AB3', fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                              {u.persona_id.substring(0, 8)}...
                            </span>
                          )}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td data-label="Created" title={u.created_at}>
                      {formatVirtualUserCreatedAt(u.created_at)}
                    </td>
                    <td className="id-cell" data-label="ID" title={u.id}>
                      {u.id}
                    </td>
                    <td className="actions-cell" data-label="Actions">
                      <div className="actions-wrap">
                        <button
                          className="edit-btn"
                          onClick={() => {
                            setSelectedVirtualUserId(u.id);
                            setTimeout(() => personasSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
                          }}
                        >
                          <i className="fas fa-user-tag"></i> Personas
                        </button>
                        <button
                          className="edit-btn"
                          onClick={() => void handleOpenEditModal(u)}
                          disabled={loading}
                          title="Edit virtual user"
                        >
                          <i className="fas fa-edit"></i> Edit
                        </button>
                        <button className="edit-btn" onClick={() => handleDeleteVirtualUser(u.id)} disabled={loading}>
                          <i className="fas fa-trash"></i> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Personas */}
      <div id="vuser-personas-section" style={{ marginBottom: 24 }} ref={personasSectionRef}>
        <div className="admin-section-header" style={{ marginBottom: 12 }}>
          <h2 style={{ fontSize: 20 }}>Personas {selectedVirtualUser ? `for ${selectedVirtualUser.name}` : ''}</h2>
          <div className="header-actions">
            <button
              className="action-btn apply-btn"
              onClick={() => {
                setCreatePersona(createDefaultPersonaFormState());
                setCreatePersonaOpen(true);
              }}
              disabled={loading || !selectedVirtualUser}
              title={!selectedVirtualUser ? 'Select a virtual user first' : undefined}
            >
              <i className="fas fa-plus" aria-hidden="true"></i>
              <span className="btn-text">Create</span>
            </button>
          </div>
        </div>

        {!selectedVirtualUser ? (
          <div className="admin-message error" style={{ margin: 0 }}>
            <i className="fas fa-exclamation-circle"></i> Select a virtual user to manage personas.
          </div>
        ) : (
          <>
            {/* Duplicate error near this section so it's visible even after scrolling */}
            {error && !createVuOpen && !editVuOpen && !createPersonaOpen && !editPersonaOpen && (
              <div className="admin-message error" style={{ margin: 0, marginBottom: 16 }}>
                <i className="fas fa-exclamation-circle"></i> {error}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
              <select 
                value={assignPersonaId} 
                onChange={(e) => setAssignPersonaId(e.target.value)} 
                style={{ 
                  minHeight: 44, 
                  borderRadius: 10, 
                  padding: '10px 12px',
                  flex: '1 1 auto',
                  minWidth: '200px',
                  border: '2px solid #E3F0FB',
                  fontSize: '14px'
                }}
                disabled={personas.length === 0 || loading}
              >
                <option value="" disabled>
                  {personas.length === 0 ? 'No personas available' : 'Select persona to assign…'}
                </option>
                {personas.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.id}) - {p.type.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
              <button className="action-btn refresh-btn" onClick={() => void handleAssignPersona()} disabled={loading || !assignPersonaId || personas.length === 0}>
                <i className="fas fa-link" aria-hidden="true"></i>
                <span className="btn-text">Assign</span>
              </button>
            </div>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>ID</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {personas.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="empty-cell">
                        <div className="empty-state">
                          <div className="empty-title">No personas</div>
                          <div className="empty-subtitle">Create one and assign it to the virtual user.</div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    personas.map((p) => (
                      <tr key={p.id}>
                        <td data-label="Name">{p.name}</td>
                        <td data-label="Type">{p.type.replace(/_/g, ' ')}</td>
                        <td data-label="Description" className="description-cell">
                          <TruncatedText text={p.description} maxLength={100} />
                        </td>
                        <td className="id-cell" data-label="ID" title={p.id}>
                          {p.id}
                        </td>
                        <td className="actions-cell" data-label="Actions">
                          <div className="actions-wrap">
                            <button
                              className="edit-btn"
                              onClick={() => handleOpenEditPersonaModal(p)}
                              disabled={loading}
                              title="Edit persona"
                            >
                              <i className="fas fa-edit"></i> Edit
                            </button>
                            <button
                              className="edit-btn"
                              onClick={() => handleDeletePersona(p.id)}
                              disabled={loading}
                              title="Delete persona"
                            >
                              <i className="fas fa-trash"></i> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>


      {/* Create virtual user modal */}
      {createVuOpen && (
        <div className="modal-overlay">
          <div className="modal-content modal-content--extra-wide">
            <div className="modal-header">
              <h3>Create virtual user</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setCreateVuOpen(false);
                  // Reset form when closing modal
                  setCreateVu(createDefaultCreateVuForm());
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              {createVuError && (
                <div className="admin-message error" style={{ margin: '0 0 16px 0' }}>
                  <i className="fas fa-exclamation-circle"></i> {createVuError}
                </div>
              )}
              <div className="form-group">
                <label>Type</label>
                <select value={createVu.type} onChange={(e) => setCreateVu((p) => ({ ...p, type: e.target.value as any }))}>
                  <option value="user">user</option>
                  <option value="admin">admin</option>
                </select>
              </div>
              <div className="form-group">
                <label>Name</label>
                <input 
                  value={createVu.name} 
                  onChange={(e) => setCreateVu((p) => ({ ...p, name: e.target.value.slice(0, 200) }))} 
                  maxLength={200}
                />
                <div className={`form-char-count ${createVu.name.length > 180 ? 'warning' : ''} ${createVu.name.length >= 200 ? 'error' : ''}`}>
                  {createVu.name.length}/200
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <input 
                  value={createVu.description} 
                  onChange={(e) => setCreateVu((p) => ({ ...p, description: e.target.value.slice(0, 200) }))} 
                  maxLength={200}
                />
                <div className={`form-char-count ${createVu.description.length > 180 ? 'warning' : ''} ${createVu.description.length >= 200 ? 'error' : ''}`}>
                  {createVu.description.length}/200
                </div>
              </div>
              <VUserToolPreferencesFields
                value={createVu}
                onChange={(next) => setCreateVu((prev) => ({ ...prev, ...next }))}
              />
              {createVu.type !== 'admin' && (
                <div className="form-group">
                  <label style={{ fontWeight: 'bold', marginBottom: '12px', display: 'block' }}>AI Configuration</label>
                  <MiniAgentConfigurator
                    integrationId={integrationId}
                    value={createVu.ai_config}
                    onChange={(config) => setCreateVu((p) => ({ ...p, ai_config: config }))}
                  />
                </div>
              )}
              <div className="vuser-prompt-block">
                {createVu.type !== 'admin' ? (
                  <p className="vuser-prompt-block__hint">
                    Default prompts (English) are pre-filled for virtual users. Clear a field to restore the default on save.
                  </p>
                ) : null}
                <label>
                  {createVu.type === 'admin' ? (
                    <>
                      CORE_PROMPT
                      <span>(optional, max {VU_PROMPT_MAX} characters — role description)</span>
                    </>
                  ) : (
                    <>
                      Virtual user system prompt
                      <span>(optional, max {VU_PROMPT_MAX} characters)</span>
                    </>
                  )}
                </label>
                <textarea
                  rows={4}
                  value={createVu.virtual_user_system_prompt}
                  onChange={(e) =>
                    setCreateVu((p) => ({
                      ...p,
                      virtual_user_system_prompt: e.target.value.slice(0, VU_PROMPT_MAX),
                    }))
                  }
                  maxLength={VU_PROMPT_MAX}
                  placeholder={
                    createVu.type === 'admin'
                      ? 'Describe the virtual admin role. Saved as CORE_PROMPT in user_config and as the system prompt for the service.'
                      : 'Stored separately from user_config…'
                  }
                />
                <VuPromptCharCount length={createVu.virtual_user_system_prompt.length} max={VU_PROMPT_MAX} />
              </div>
              {createVu.type !== 'admin' && (
                <>
                  <div className="vuser-prompt-block">
                    <label>
                      First user message instruction
                      <span>(optional, max {VU_PROMPT_MAX} characters)</span>
                    </label>
                    <textarea
                      rows={4}
                      value={createVu.first_user_message_instruction}
                      onChange={(e) =>
                        setCreateVu((p) => ({
                          ...p,
                          first_user_message_instruction: e.target.value.slice(0, VU_PROMPT_MAX),
                        }))
                      }
                      maxLength={VU_PROMPT_MAX}
                    />
                    <VuPromptCharCount length={createVu.first_user_message_instruction.length} max={VU_PROMPT_MAX} />
                  </div>
                  <div className="vuser-prompt-block">
                    <label>
                      Follow-up user message instruction
                      <span>(optional, max {VU_PROMPT_MAX} characters)</span>
                    </label>
                    <textarea
                      rows={4}
                      value={createVu.followup_user_message_instruction}
                      onChange={(e) =>
                        setCreateVu((p) => ({
                          ...p,
                          followup_user_message_instruction: e.target.value.slice(0, VU_PROMPT_MAX),
                        }))
                      }
                      maxLength={VU_PROMPT_MAX}
                    />
                    <VuPromptCharCount length={createVu.followup_user_message_instruction.length} max={VU_PROMPT_MAX} />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => {
                  setCreateVuOpen(false);
                  setCreateVuError(null);
                  // Reset form when closing modal
                  setCreateVu(createDefaultCreateVuForm());
                }}
              >
                Cancel
              </button>
              <button className="btn-save" onClick={() => void handleCreateVirtualUser()} disabled={loading}>
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit virtual user modal */}
      {editVuOpen && editingVuId && (
        <div className="modal-overlay">
          <div className="modal-content modal-content--extra-wide">
            <div className="modal-header">
              <h3>Edit virtual user</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setEditVuOpen(false);
                  setEditingVuId(null);
                  setEditVu(createDefaultEditVuForm());
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              {editVuError && (
                <div className="admin-message error" style={{ margin: '0 0 16px 0' }}>
                  <i className="fas fa-exclamation-circle"></i> {editVuError}
                </div>
              )}
              <div className="form-group">
                <label>Type (read-only)</label>
                <input
                  type="text"
                  value={virtualUsers.find((u) => u.id === editingVuId)?.type || '-'}
                  disabled
                  style={{ background: '#F5F9FE', cursor: 'not-allowed' }}
                />
              </div>
              <div className="form-group">
                <label>Name</label>
                <input 
                  value={editVu.name} 
                  onChange={(e) => setEditVu((p) => ({ ...p, name: e.target.value.slice(0, 200) }))} 
                  maxLength={200}
                />
                <div className={`form-char-count ${editVu.name.length > 180 ? 'warning' : ''} ${editVu.name.length >= 200 ? 'error' : ''}`}>
                  {editVu.name.length}/200
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <input 
                  value={editVu.description} 
                  onChange={(e) => setEditVu((p) => ({ ...p, description: e.target.value.slice(0, 200) }))} 
                  maxLength={200}
                />
                <div className={`form-char-count ${editVu.description.length > 180 ? 'warning' : ''} ${editVu.description.length >= 200 ? 'error' : ''}`}>
                  {editVu.description.length}/200
                </div>
              </div>
              <VUserToolPreferencesFields
                value={editVu}
                onChange={(next) => setEditVu((prev) => ({ ...prev, ...next }))}
              />
              {virtualUsers.find((u) => u.id === editingVuId)?.type !== 'admin' && (
                <div className="form-group">
                  <label style={{ fontWeight: 'bold', marginBottom: '12px', display: 'block' }}>AI Configuration</label>
                  <MiniAgentConfigurator
                    integrationId={integrationId}
                    value={editVu.ai_config}
                    onChange={(config) => setEditVu((p) => ({ ...p, ai_config: config }))}
                  />
                </div>
              )}
              <div className="vuser-prompt-block">
                {virtualUsers.find((u) => u.id === editingVuId)?.type !== 'admin' ? (
                  <p className="vuser-prompt-block__hint">
                    Default prompts (English) are used when fields are empty. Clear a field to restore the default on save.
                  </p>
                ) : null}
                <label>
                  {virtualUsers.find((u) => u.id === editingVuId)?.type === 'admin' ? (
                    <>
                      CORE_PROMPT
                      <span>(max {VU_PROMPT_MAX} characters — role description)</span>
                    </>
                  ) : (
                    <>
                      Virtual user system prompt
                      <span>(max {VU_PROMPT_MAX} characters)</span>
                    </>
                  )}
                </label>
                <textarea
                  rows={4}
                  value={editVu.virtual_user_system_prompt}
                  onChange={(e) =>
                    setEditVu((p) => ({
                      ...p,
                      virtual_user_system_prompt: e.target.value.slice(0, VU_PROMPT_MAX),
                    }))
                  }
                  maxLength={VU_PROMPT_MAX}
                  placeholder={
                    virtualUsers.find((u) => u.id === editingVuId)?.type === 'admin'
                      ? 'Role description for the virtual admin. Cleared field removes CORE_PROMPT and system prompt when saved.'
                      : 'English default is used when empty on save.'
                  }
                />
                <VuPromptCharCount length={editVu.virtual_user_system_prompt.length} max={VU_PROMPT_MAX} />
              </div>
              {virtualUsers.find((u) => u.id === editingVuId)?.type !== 'admin' && (
                <>
                  <div className="vuser-prompt-block">
                    <label>
                      First user message instruction
                      <span>(max {VU_PROMPT_MAX} characters)</span>
                    </label>
                    <textarea
                      rows={4}
                      value={editVu.first_user_message_instruction}
                      onChange={(e) =>
                        setEditVu((p) => ({
                          ...p,
                          first_user_message_instruction: e.target.value.slice(0, VU_PROMPT_MAX),
                        }))
                      }
                      maxLength={VU_PROMPT_MAX}
                      placeholder="Leave empty to clear (saved as null on update)"
                    />
                    <VuPromptCharCount length={editVu.first_user_message_instruction.length} max={VU_PROMPT_MAX} />
                  </div>
                  <div className="vuser-prompt-block">
                    <label>
                      Follow-up user message instruction
                      <span>(max {VU_PROMPT_MAX} characters)</span>
                    </label>
                    <textarea
                      rows={4}
                      value={editVu.followup_user_message_instruction}
                      onChange={(e) =>
                        setEditVu((p) => ({
                          ...p,
                          followup_user_message_instruction: e.target.value.slice(0, VU_PROMPT_MAX),
                        }))
                      }
                      maxLength={VU_PROMPT_MAX}
                      placeholder="Leave empty to clear (saved as null on update)"
                    />
                    <VuPromptCharCount length={editVu.followup_user_message_instruction.length} max={VU_PROMPT_MAX} />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => {
                  setEditVuOpen(false);
                  setEditingVuId(null);
                  setEditVuError(null);
                  // Reset form when closing modal
                  setEditVu(createDefaultEditVuForm());
                }}
              >
                Cancel
              </button>
              <button className="btn-save" onClick={() => void handleEditVirtualUser()} disabled={loading}>
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create persona modal */}
      {createPersonaOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Create persona</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setCreatePersonaOpen(false);
                  setCreatePersonaError(null);
                  setCreatePersona(createDefaultPersonaFormState());
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              {createPersonaError && (
                <div className="admin-message error" style={{ margin: '0 0 16px 0' }}>
                  <i className="fas fa-exclamation-circle"></i> {createPersonaError}
                </div>
              )}
              <div className="form-group">
                <label>Name</label>
                <input 
                  value={createPersona.name} 
                  onChange={(e) => setCreatePersona((p) => ({ ...p, name: e.target.value.slice(0, 200) }))} 
                  maxLength={200}
                />
                <div className={`form-char-count ${createPersona.name.length > 180 ? 'warning' : ''} ${createPersona.name.length >= 200 ? 'error' : ''}`}>
                  {createPersona.name.length}/200
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <input 
                  value={createPersona.description} 
                  onChange={(e) => setCreatePersona((p) => ({ ...p, description: e.target.value.slice(0, 200) }))} 
                  maxLength={200}
                />
                <div className={`form-char-count ${createPersona.description.length > 180 ? 'warning' : ''} ${createPersona.description.length >= 200 ? 'error' : ''}`}>
                  {createPersona.description.length}/200
                </div>
              </div>
              <div className="form-group">
                <label style={{ fontWeight: 'bold', marginBottom: '16px', display: 'block', fontSize: '14px' }}>Reaction Probabilities</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'stretch' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <label style={{ fontSize: '13px', marginBottom: '8px', display: 'block', fontWeight: '500', lineHeight: '1.4', minHeight: '36px' }}>Like Probability</label>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={createPersona.reaction_rules.like_probability || 0}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 0 && value <= 1) {
                          setCreatePersona((p) => ({ ...p, reaction_rules: { ...p.reaction_rules, like_probability: value } }));
                        }
                      }}
                      style={{ 
                        width: '100%', 
                        height: '44px',
                        padding: '10px 12px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <label style={{ fontSize: '13px', marginBottom: '8px', display: 'block', fontWeight: '500', lineHeight: '1.4', minHeight: '36px' }}>Dislike Probability</label>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={createPersona.reaction_rules.dislike_probability || 0}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 0 && value <= 1) {
                          setCreatePersona((p) => ({ ...p, reaction_rules: { ...p.reaction_rules, dislike_probability: value } }));
                        }
                      }}
                      style={{ 
                        width: '100%', 
                        height: '44px',
                        padding: '10px 12px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <label style={{ fontSize: '13px', marginBottom: '8px', display: 'block', fontWeight: '500', lineHeight: '1.4', minHeight: '36px' }}>Comment Probability</label>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={createPersona.reaction_rules.comment_probability || 0}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 0 && value <= 1) {
                          setCreatePersona((p) => ({ ...p, reaction_rules: { ...p.reaction_rules, comment_probability: value } }));
                        }
                      }}
                      style={{ 
                        width: '100%', 
                        height: '44px',
                        padding: '10px 12px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <label style={{ fontSize: '13px', marginBottom: '8px', display: 'block', fontWeight: '500', lineHeight: '1.4', minHeight: '36px' }}>Reference Answer Probability</label>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={createPersona.reaction_rules.reference_answer_probability || 0}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 0 && value <= 1) {
                          setCreatePersona((p) => ({ ...p, reaction_rules: { ...p.reaction_rules, reference_answer_probability: value } }));
                        }
                      }}
                      style={{ 
                        width: '100%', 
                        height: '44px',
                        padding: '10px 12px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label style={{ fontWeight: 'bold', marginBottom: '16px', display: 'block', fontSize: '14px' }}>Reaction Prompts (Optional)</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '13px', marginBottom: '6px', display: 'block', fontWeight: '500' }}>Likes Prompt</label>
                    <textarea
                      rows={2}
                      value={createPersona.judge_meta_prompts?.like_prompt || ''}
                      onChange={(e) => setCreatePersona((p) => ({ ...p, judge_meta_prompts: { ...p.judge_meta_prompts, like_prompt: e.target.value } }))}
                      placeholder={DEFAULT_PERSONA_JUDGE_META_PROMPTS.like_prompt}
                      style={{ width: '100%', resize: 'vertical' }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '13px', marginBottom: '6px', display: 'block', fontWeight: '500' }}>Dislikes Prompt</label>
                    <textarea
                      rows={2}
                      value={createPersona.judge_meta_prompts?.dislike_prompt || ''}
                      onChange={(e) => setCreatePersona((p) => ({ ...p, judge_meta_prompts: { ...p.judge_meta_prompts, dislike_prompt: e.target.value } }))}
                      placeholder={DEFAULT_PERSONA_JUDGE_META_PROMPTS.dislike_prompt}
                      style={{ width: '100%', resize: 'vertical' }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '13px', marginBottom: '6px', display: 'block', fontWeight: '500' }}>Comment Prompt</label>
                    <textarea
                      rows={2}
                      value={createPersona.judge_meta_prompts?.comment_prompt || ''}
                      onChange={(e) => setCreatePersona((p) => ({ ...p, judge_meta_prompts: { ...p.judge_meta_prompts, comment_prompt: e.target.value } }))}
                      placeholder={DEFAULT_PERSONA_JUDGE_META_PROMPTS.comment_prompt}
                      style={{ width: '100%', resize: 'vertical' }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '13px', marginBottom: '6px', display: 'block', fontWeight: '500' }}>Reference Answer Prompt</label>
                    <textarea
                      rows={2}
                      value={createPersona.judge_meta_prompts?.reference_answer_prompt || ''}
                      onChange={(e) => setCreatePersona((p) => ({ ...p, judge_meta_prompts: { ...p.judge_meta_prompts, reference_answer_prompt: e.target.value } }))}
                      placeholder="Optional prompt for reference answers..."
                      style={{ width: '100%', resize: 'vertical' }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => {
                  setCreatePersonaOpen(false);
                  setCreatePersonaError(null);
                  setCreatePersona(createDefaultPersonaFormState());
                }}
              >
                Cancel
              </button>
              <button className="btn-save" onClick={() => void handleCreatePersona()} disabled={loading || !selectedVirtualUser}>
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit persona modal */}
      {editPersonaOpen && editingPersonaId && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit persona</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setEditPersonaOpen(false);
                  setEditingPersonaId(null);
                  setEditPersonaError(null);
                  setEditPersona(createDefaultPersonaFormState());
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              {editPersonaError && (
                <div className="admin-message error" style={{ margin: '0 0 16px 0' }}>
                  <i className="fas fa-exclamation-circle"></i> {editPersonaError}
                </div>
              )}
              <div className="form-group">
                <label>Name</label>
                <input 
                  value={editPersona.name} 
                  onChange={(e) => setEditPersona((p) => ({ ...p, name: e.target.value.slice(0, 200) }))} 
                  maxLength={200}
                />
                <div className={`form-char-count ${editPersona.name.length > 180 ? 'warning' : ''} ${editPersona.name.length >= 200 ? 'error' : ''}`}>
                  {editPersona.name.length}/200
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <input 
                  value={editPersona.description} 
                  onChange={(e) => setEditPersona((p) => ({ ...p, description: e.target.value.slice(0, 200) }))} 
                  maxLength={200}
                />
                <div className={`form-char-count ${editPersona.description.length > 180 ? 'warning' : ''} ${editPersona.description.length >= 200 ? 'error' : ''}`}>
                  {editPersona.description.length}/200
                </div>
              </div>
              <div className="form-group">
                <label style={{ fontWeight: 'bold', marginBottom: '16px', display: 'block', fontSize: '14px' }}>Reaction Probabilities</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'stretch' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <label style={{ fontSize: '13px', marginBottom: '8px', display: 'block', fontWeight: '500', lineHeight: '1.4', minHeight: '36px' }}>Like Probability</label>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={editPersona.reaction_rules.like_probability || 0}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 0 && value <= 1) {
                          setEditPersona((p) => ({ ...p, reaction_rules: { ...p.reaction_rules, like_probability: value } }));
                        }
                      }}
                      style={{ 
                        width: '100%', 
                        height: '44px',
                        padding: '10px 12px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <label style={{ fontSize: '13px', marginBottom: '8px', display: 'block', fontWeight: '500', lineHeight: '1.4', minHeight: '36px' }}>Dislike Probability</label>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={editPersona.reaction_rules.dislike_probability || 0}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 0 && value <= 1) {
                          setEditPersona((p) => ({ ...p, reaction_rules: { ...p.reaction_rules, dislike_probability: value } }));
                        }
                      }}
                      style={{ 
                        width: '100%', 
                        height: '44px',
                        padding: '10px 12px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <label style={{ fontSize: '13px', marginBottom: '8px', display: 'block', fontWeight: '500', lineHeight: '1.4', minHeight: '36px' }}>Comment Probability</label>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={editPersona.reaction_rules.comment_probability || 0}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 0 && value <= 1) {
                          setEditPersona((p) => ({ ...p, reaction_rules: { ...p.reaction_rules, comment_probability: value } }));
                        }
                      }}
                      style={{ 
                        width: '100%', 
                        height: '44px',
                        padding: '10px 12px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <label style={{ fontSize: '13px', marginBottom: '8px', display: 'block', fontWeight: '500', lineHeight: '1.4', minHeight: '36px' }}>Reference Answer Probability</label>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={editPersona.reaction_rules.reference_answer_probability || 0}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 0 && value <= 1) {
                          setEditPersona((p) => ({ ...p, reaction_rules: { ...p.reaction_rules, reference_answer_probability: value } }));
                        }
                      }}
                      style={{ 
                        width: '100%', 
                        height: '44px',
                        padding: '10px 12px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label style={{ fontWeight: 'bold', marginBottom: '16px', display: 'block', fontSize: '14px' }}>Reaction Prompts (Optional)</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '13px', marginBottom: '6px', display: 'block', fontWeight: '500' }}>Likes Prompt</label>
                    <textarea
                      rows={2}
                      value={editPersona.judge_meta_prompts?.like_prompt || ''}
                      onChange={(e) => setEditPersona((p) => ({ ...p, judge_meta_prompts: { ...p.judge_meta_prompts, like_prompt: e.target.value } }))}
                      placeholder={DEFAULT_PERSONA_JUDGE_META_PROMPTS.like_prompt}
                      style={{ width: '100%', resize: 'vertical' }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '13px', marginBottom: '6px', display: 'block', fontWeight: '500' }}>Dislikes Prompt</label>
                    <textarea
                      rows={2}
                      value={editPersona.judge_meta_prompts?.dislike_prompt || ''}
                      onChange={(e) => setEditPersona((p) => ({ ...p, judge_meta_prompts: { ...p.judge_meta_prompts, dislike_prompt: e.target.value } }))}
                      placeholder={DEFAULT_PERSONA_JUDGE_META_PROMPTS.dislike_prompt}
                      style={{ width: '100%', resize: 'vertical' }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '13px', marginBottom: '6px', display: 'block', fontWeight: '500' }}>Comment Prompt</label>
                    <textarea
                      rows={2}
                      value={editPersona.judge_meta_prompts?.comment_prompt || ''}
                      onChange={(e) => setEditPersona((p) => ({ ...p, judge_meta_prompts: { ...p.judge_meta_prompts, comment_prompt: e.target.value } }))}
                      placeholder={DEFAULT_PERSONA_JUDGE_META_PROMPTS.comment_prompt}
                      style={{ width: '100%', resize: 'vertical' }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '13px', marginBottom: '6px', display: 'block', fontWeight: '500' }}>Reference Answer Prompt</label>
                    <textarea
                      rows={2}
                      value={editPersona.judge_meta_prompts?.reference_answer_prompt || ''}
                      onChange={(e) => setEditPersona((p) => ({ ...p, judge_meta_prompts: { ...p.judge_meta_prompts, reference_answer_prompt: e.target.value } }))}
                      placeholder="Optional prompt for reference answers..."
                      style={{ width: '100%', resize: 'vertical' }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => {
                  setEditPersonaOpen(false);
                  setEditingPersonaId(null);
                  setEditPersonaError(null);
                  setEditPersona(createDefaultPersonaFormState());
                }}
              >
                Cancel
              </button>
              <button className="btn-save" onClick={() => void handleEditPersona()} disabled={loading}>
                Update
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Delete Persona Confirmation Modal */}
      <ConfirmModal
        isOpen={deletePersonaModal.isOpen}
        title="Delete Persona"
        message="Are you sure you want to delete this persona? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isConfirmLoading={loading}
        onConfirm={confirmDeletePersona}
        onCancel={() => setDeletePersonaModal({ isOpen: false, personaId: null })}
      />

      {/* Delete Virtual User Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteVirtualUserModal.isOpen}
        title="Delete Virtual User"
        message="Are you sure you want to delete this virtual user? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isConfirmLoading={loading}
        onConfirm={confirmDeleteVirtualUser}
        onCancel={() => setDeleteVirtualUserModal({ isOpen: false, virtualUserId: null })}
      />
    </div>
  );
};

export default VUserPanel;

